import { createAction, isOfType } from 'deox';
import { asActionMessage, isActionMessage } from './ActionMessage.js';
import { without, any } from 'ramda';
import matchOrigin from './utils/matchOrigin.js';

/** Similar to TCPs' SYN and SYN-ACK */
/** @internal */
var sayHello = createAction('SAY_HELLO', function (resolve) { return function (data) {
    return resolve(data);
}; });
/** @internal */
var Messenger = /** @class */ (function () {
    function Messenger(options) {
        var _this = this;
        this._targetOrigin = null;
        this._targetOrigins = [];
        this.ready = false;
        this.actionCallbacks = [];
        this.readyCallbacks = [];
        this.receiveMessage = function (event) {
            if (!(_this.isHelloMessage(event) || _this.isValidMessage(event))) {
                return;
            }
            var action = event.data.action;
            if (!_this.isReady && !isOfType(sayHello, action)) {
                console.warn('Receive action before ready', action);
            }
            _this.actionCallbacks.forEach(function (callback) { return callback(action); });
        };
        this.isValidMessage = function (event) {
            return (_this.targetOrigin &&
                event.origin === _this._targetOrigin &&
                event.source == _this.targetWindow &&
                isActionMessage(event.data));
        };
        this.ownName = options.ownName;
        this.ownWindow = options.ownWindow;
        this.targetWindow = options.targetWindow;
        if ('targetOrigin' in options) {
            this._targetOrigin = options.targetOrigin;
        }
        else {
            this._targetOrigins = options.targetOrigins;
        }
        this.ready = Boolean(options.isReady);
        this.ownWindow.addEventListener('message', this.receiveMessage);
        if (this.isReady) {
            this.setReady(true);
        }
        else {
            this.shakeHand();
        }
    }
    Object.defineProperty(Messenger.prototype, "isReady", {
        get: function () {
            return this.ready;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Messenger.prototype, "targetOrigin", {
        get: function () {
            return this._targetOrigin;
        },
        enumerable: false,
        configurable: true
    });
    Messenger.prototype.send = function (action) {
        if (!this.ready) {
            console.warn('Sending action before the target is ready.', action);
        }
        if (this._targetOrigin === null) {
            throw new Error("sending message when targetOrigin has not been set " + JSON.stringify(action));
        }
        this.targetWindow.postMessage(asActionMessage(action), this._targetOrigin);
    };
    Messenger.prototype.onAction = function (callback) {
        this.actionCallbacks = this.actionCallbacks.concat(callback);
    };
    Messenger.prototype.offAction = function (callback) {
        this.actionCallbacks = without([callback], this.actionCallbacks);
    };
    Messenger.prototype.onReady = function (callback) {
        this.readyCallbacks = this.readyCallbacks.concat(callback);
    };
    Messenger.prototype.offReady = function (callback) {
        this.readyCallbacks = without([callback], this.readyCallbacks);
    };
    Messenger.prototype.setReady = function (isReady) {
        this.ready = isReady;
        if (isReady) {
            this.readyCallbacks.forEach(function (cb) { return cb(); });
        }
        this.readyCallbacks = [];
    };
    /**
     * Two-way handshake that can be initiated by either ownWindow or the
     * targetWindow to let both windows know that they are ready to receive
     * messages.
     *
     * 1. On initialize, send a HELLO message. If the target has already been
     * initialized, we will acknowledge the message. Otherwise, we'll wait for
     * its HELLO message.
     *
     * 2. Wait for the target to say HELLO or acknowledge.
     *
     * 3. If the target send an initial HELLO message, acknowledge it, because
     * it hasn't receive our initial message. If the target send an
     * acknowledgement, it must have received our initial message and we don't
     * have to do anything.
     *
     * NOTE:
     * The targetWindow may send another unacknowledged HELLO message, which
     * we will acknowledge. This may happens if the iframe navigate and try to
     * re-initialize. Only the same origin as the first HELLO message is allowed.
     */
    Messenger.prototype.shakeHand = function () {
        var _this = this;
        this.sendHello(false);
        var onHello = function (event) {
            if (!_this.isHelloMessage(event))
                return;
            var action = event.data.action;
            if (!isOfType(sayHello, action))
                return;
            if (!_this._targetOrigin) {
                _this._targetOrigin = event.origin;
            }
            if (!action.payload.ack) {
                _this.sendHello(true);
            }
            _this.setReady(true);
        };
        this.ownWindow.addEventListener('message', onHello);
    };
    Messenger.prototype.sendHello = function (ack) {
        var action = sayHello({
            name: this.ownName,
            ack: ack,
        });
        // If the targetOrigin has not been set, e.g., when connecting to
        // a parent frame. We'll send the HELLO message with no origin preference.
        // This assume that the targetWindow is trusted (e.g. targetWindow is a
        // parent, and we have Content-Security-Policy set properly)
        try {
            this.targetWindow.postMessage(asActionMessage(action), this._targetOrigin || '*');
        }
        catch (e) {
            console.warn('Failed to start connection with', this._targetOrigin);
        }
    };
    Messenger.prototype.isHelloMessage = function (event) {
        /**
         * When receiving HELLO message, we'll allow any origin that matches
         * the whitelist. For any other other messages, the origin must match the
         * one initialized.
         */
        var originMatches = event.origin === this._targetOrigin;
        if (!this._targetOrigin) {
            originMatches = any(matchOrigin(event.origin), this._targetOrigins);
        }
        return (originMatches &&
            event.source == this.targetWindow &&
            isActionMessage(event.data) &&
            isOfType(sayHello, event.data.action));
    };
    return Messenger;
}());

export default Messenger;
export { sayHello };
